/**
 * API utility providing authenticated HTTP requests to the backend.
 * Handles Auth0 token management and common request patterns.
 * 
 * Key Features:
 * - Automatic Auth0 token injection
 * - Error handling and logging
 * - Common HTTP methods (GET, POST, PUT, DELETE)
 * - Content-Type management
 */

import axios, { AxiosInstance } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

// Ensure we're using HTTPS for the API URL
const API_BASE_URL = (() => {
  const url = process.env.REACT_APP_API_URL;
  if (!url) {
    console.error('REACT_APP_API_URL environment variable is not set');
    throw new Error('API URL is not configured');
  }
  try {
    const urlObj = new URL(url);
    urlObj.protocol = 'https:';
    return urlObj.toString();
  } catch (error) {
    console.error('Invalid API URL:', url);
    throw new Error(`Invalid API URL configuration: ${url}`);
  }
})();

// Returns auth headers, preferring admin impersonation over Auth0 when active
const buildAuthHeaders = async (
  getAuth0Token: () => Promise<string>
): Promise<Record<string, string>> => {
  const impersonateUserId =
    typeof window !== 'undefined'
      ? sessionStorage.getItem('impersonateUserId')
      : null;
  if (impersonateUserId) {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      return {
        Authorization: `Bearer ${adminToken}`,
        'X-Impersonate-User-Id': impersonateUserId,
      };
    }
  }
  const token = await getAuth0Token();
  return { Authorization: `Bearer ${token}` };
};

export const useApi = () => {
  const { getAccessTokenSilently } = useAuth0();

  const createAxiosInstance = async (): Promise<AxiosInstance> => {
    const authHeaders = await buildAuthHeaders(getAccessTokenSilently);
    return axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
    });
  };

  return {
    get: async (endpoint: string, config?: any) => {
      const api = await createAxiosInstance();
      return api.get(endpoint, config);
    },

    post: async (endpoint: string, data: any) => {
      const authHeaders = await buildAuthHeaders(getAccessTokenSilently);
      const headers: any = { ...authHeaders };
      // Don't set Content-Type for FormData - let browser set it with boundary
      if (!(data instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }
      const api = axios.create({
        baseURL: API_BASE_URL,
        headers,
      });
      return api.post(endpoint, data);
    },

    put: async (endpoint: string, data: any) => {
      const api = await createAxiosInstance();
      return api.put(endpoint, data);
    },

    delete: async (endpoint: string, data?: any) => {
      const api = await createAxiosInstance();
      return api.delete(endpoint, { data });
    },
  };
};

// API endpoints
export const API_ENDPOINTS = {


  AUTH_COMPLETE_SIGNUP: '/api/auth/complete-signup',
  AUTH_PROFILE_STATUS: '/api/auth/profile-status',
  AUTH_SEND_EMAIL_CODE: '/api/auth/send-email-code',
  AUTH_VERIFY_EMAIL_CODE: '/api/auth/verify-email-code',
  // Facebook Pages
  FACEBOOK_PAGES: '/api/facebook/pages',
  FACEBOOK_PAGE_DETAIL: (pageId: string) => `/api/facebook/pages/${pageId}`,
  FACEBOOK_PAGE_PROMPT: (pageId: string) => `/api/facebook/pages/${pageId}/prompt`,
  FACEBOOK_PAGE_PROMPT_HISTORY: (pageId: string) => `/api/facebook/pages/${pageId}/prompt-history`,
  FACEBOOK_PAGE_ACTIVATE: (pageId: string) => `/api/facebook/pages/${pageId}/activate`,
  FACEBOOK_PAGE_DEACTIVATE: (pageId: string) => `/api/facebook/pages/${pageId}/deactivate`,
  FACEBOOK_PAGE_PERMANENT_DELETE: (pageId: string) => `/api/facebook/pages/${pageId}/permanent-delete`,
  
  // Conversations and Messages - all endpoints use plural 'conversations'
  PAGE_CONVERSATIONS: (pageId: string) => `/api/facebook/pages/${pageId}/conversations`,
  CONVERSATION_MESSAGES: (conversationId: string) => `/api/conversations/${conversationId}/messages`,
  SEND_MESSAGE: (conversationId: string) => `/api/conversations/${conversationId}/messages`,
  GENERATE_REPLY: (conversationId: string, messageId: string) => `/api/conversations/${conversationId}/messages/${messageId}/generate-reply`,
  SIMULATE_MESSAGE: (pageId: string) => `/api/facebook/pages/${pageId}/simulate-message`,
  PAGE_MESSAGES: (pageId: string) => `/api/facebook/pages/${pageId}/messages`,
  
  // Comments
  PAGE_COMMENTS: (pageId: string) => `/api/facebook/pages/${pageId}/comments`,
  COMMENT_ACTION: (pageId: string, commentId: string, action: string) => `/api/facebook/pages/${pageId}/comments/${commentId}/${action}`,
  BULK_COMMENT_ACTION: (pageId: string) => `/api/facebook/pages/${pageId}/comments/bulk-action`,
  RATE_LIMIT: (pageId: string) => `/api/facebook/pages/${pageId}/rate-limit`,
  COMMENT_AI_REPLY: (pageId: string) => `/api/facebook/pages/${pageId}/comments/ai-reply`,
  PAGE_POSTS: (pageId: string) => `/api/facebook/pages/${pageId}/posts`,
  
  // OAuth
  FACEBOOK_OAUTH_START: '/facebook/oauth/start',
  FACEBOOK_OAUTH_EXCHANGE: '/facebook/oauth/exchange',

  // Instagram OAuth (Instagram Login for Business — beta-gated)
  INSTAGRAM_OAUTH_START: '/instagram/oauth/start',
  INSTAGRAM_OAUTH_EXCHANGE: '/instagram/oauth/exchange',
  
  // Settings
  USER_SETTINGS: '/api/user/settings',
  USER_LAST_PAGE: '/api/user/last-page',
  AI_SETTINGS: (pageId: string) => `/api/facebook/pages/${pageId}/ai-settings`,
  AI_INSTRUCTIONS: (pageId: string) => `/api/facebook/pages/${pageId}/ai-instructions`,
  AI_INSTRUCTION: (pageId: string, instructionId: string) => `/api/facebook/pages/${pageId}/ai-instructions/${instructionId}`,
  AI_INSTRUCTION_DEFAULT: (pageId: string) => `/api/facebook/pages/${pageId}/ai-instructions/default`,
  CUSTOMER_AI_SETTINGS: (pageId: string, customerId: string) => 
    `/api/facebook/pages/${pageId}/customers/${customerId}/ai-settings`,
  CUSTOMER_RESET_COUNTER: (pageId: string, customerId: string) => 
    `/api/facebook/pages/${pageId}/customers/${customerId}/reset-counter`,
  TEST_CUSTOMER_INFO: (pageId: string) => `/api/facebook/pages/${pageId}/test-customer-info`,
  TEST_AI_EXTRACTION: (pageId: string) => `/api/facebook/pages/${pageId}/test-ai-extraction`,
  TEST_LANGUAGE: (pageId: string) => `/api/facebook/pages/${pageId}/test-language`,
  
  // AI Context Resources
  AI_CONTEXT: (pageId: string) => `/api/facebook/pages/${pageId}/ai-context`,
  AI_CONTEXT_UPLOAD: (pageId: string) => `/api/facebook/pages/${pageId}/ai-context/documents`,
  AI_CONTEXT_URL: (pageId: string) => `/api/facebook/pages/${pageId}/ai-context/urls`,
  AI_CONTEXT_DELETE_DOC: (pageId: string, docId: string) => `/api/facebook/pages/${pageId}/ai-context/documents/${docId}`,
  AI_CONTEXT_DELETE_URL: (pageId: string, urlId: string) => `/api/facebook/pages/${pageId}/ai-context/urls/${urlId}`,
  
  // Test Autoreplies
  TEST_AUTOREPLIES: (pageId: string) => `/api/facebook/pages/${pageId}/test-autoreplies`,
  TEST_AUTOREPLIES_DELETE: (pageId: string, testId: string) => `/api/facebook/pages/${pageId}/test-autoreplies/${testId}`,
  
  // Moderation
  MODERATION_RULES: (pageId: string) => `/api/facebook/pages/${pageId}/moderation-rules`,
  MODERATION_STATS: (pageId: string) => `/api/facebook/pages/${pageId}/moderation-stats`,
  TEST_MODERATION: (pageId: string) => `/api/facebook/pages/${pageId}/test-moderation`,
  AI_MODERATION_STATUS: (pageId: string) => `/api/facebook/pages/${pageId}/ai-moderation-status`,

  // Platform Impact / ROI
  IMPACT: (pageId: string) => `/api/facebook/pages/${pageId}/impact`,

  // Human Review Queue
  REVIEW_QUEUE: (pageId: string) => `/api/facebook/pages/${pageId}/review`,
  REVIEW_QUEUE_COUNT: (pageId: string) => `/api/facebook/pages/${pageId}/review/count`,
  REVIEW_QUEUE_RULES: (pageId: string) => `/api/facebook/pages/${pageId}/review/rules`,
  REVIEW_ACTION: (pageId: string, commentId: string, action: string) =>
    `/api/facebook/pages/${pageId}/review/${commentId}/${action}`,
  REVIEW_BULK: (pageId: string) => `/api/facebook/pages/${pageId}/review/bulk`,
  
  // Analytics
  DASHBOARD_STATS: '/api/analytics/dashboard',
  AI_ANALYTICS: (pageId: string) => `/api/analytics/ai/${pageId}`,
  
  // Dashboard
  DASHBOARD_STATS_DETAILED: '/api/dashboard/stats',
  DASHBOARD_ACTIVITY: '/api/dashboard/activity',

  // Subscription & plans
  SUBSCRIPTION: '/api/subscription',
  SUBSCRIPTION_CHECKOUT: '/api/subscription/checkout',
  SUBSCRIPTION_PORTAL: '/api/subscription/portal',
  SUBSCRIPTION_REQUEST: '/api/subscription/request',
  PUBLIC_PLANS: '/api/plans',

  // Admin — subscriptions
  ADMIN_SUBSCRIPTION: (userId: string) => `/api/admin/subscriptions/${userId}`,
  ADMIN_SUBSCRIPTION_ACTIVATE: (userId: string) => `/api/admin/subscriptions/${userId}/activate`,
  ADMIN_SUBSCRIPTION_DEACTIVATE: (userId: string) => `/api/admin/subscriptions/${userId}/deactivate`,
  ADMIN_SUBSCRIPTION_EXTEND: (userId: string) => `/api/admin/subscriptions/${userId}/extend`,
  ADMIN_SUBSCRIPTION_PLAN: (userId: string) => `/api/admin/subscriptions/${userId}/plan`,
  ADMIN_SUBSCRIPTION_LIMITS: (userId: string) => `/api/admin/subscriptions/${userId}/limits`,
  ADMIN_SUBSCRIPTION_RESET_USAGE: (userId: string) => `/api/admin/subscriptions/${userId}/reset-usage`
};
